/**
 * Client-side image downscaler.
 *
 * Why: IDV / headshot uploads come straight from phone cameras (often
 * 4000×3000, 6–10 MB JPEGs). Streaming those to Cloudinary over mobile
 * data is slow AND the full-res preview `<img>` freezes iOS Safari for
 * a second while it decodes, which reads as a "white screen". Downscaling
 * to a sane max edge before upload cuts payload by ~85% and fixes both.
 *
 * Returns: a Promise<File> resolving to a JPEG with the original filename
 * but a reduced resolution. Falls back to the original file on any failure
 * so we never block the upload path.
 */
export const downscaleImage = async (
  file,
  { maxEdge = 1600, quality = 0.85, mimeType = 'image/jpeg' } = {},
) => {
  if (!file || !file.type?.startsWith('image/')) return file;
  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });

    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = dataUrl;
    });

    const longest = Math.max(img.width, img.height);
    // Already small enough — skip the canvas round-trip.
    if (longest <= maxEdge) return file;

    const scale = maxEdge / longest;
    const w = Math.round(img.width * scale);
    const h = Math.round(img.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, mimeType, quality),
    );
    if (!blob) return file;

    // Preserve filename so Cloudinary's public_id hints stay consistent.
    const newName = (file.name || 'upload').replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], newName, { type: mimeType, lastModified: Date.now() });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('downscaleImage failed — uploading original file', err);
    return file; // never block the upload — worst case we send original
  }
};

/**
 * Given a Cloudinary secure_url, return a thumbnail-sized variant with
 * auto-format + auto-quality so the `<img>` preview loads fast and doesn't
 * stall the main thread decoding a 6 MB JPEG.
 */
export const cloudinaryThumb = (url, width = 400) => {
  if (!url || typeof url !== 'string') return url;
  // Only rewrite Cloudinary delivery URLs — leave anything else untouched.
  const marker = '/upload/';
  const idx = url.indexOf(marker);
  if (idx === -1) return url;
  const insert = `f_auto,q_auto,w_${width},c_limit/`;
  return url.slice(0, idx + marker.length) + insert + url.slice(idx + marker.length);
};
